import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Flag, MessageSquareReply, X, ShieldAlert } from "lucide-react";
import { StarsDisplay } from "@/components/coachly/StarRating";
import { coachlyApi } from "@/lib/api";
import { COACH_PROFILE } from "@/constants/testIds";

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  const y = Math.floor(mo / 12);
  return `${y}y ago`;
}

export default function ReviewItem({ review, coachEmail, onUpdated }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState(review.coach_reply || "");
  const [replyEmail, setReplyEmail] = useState("");
  const [reported, setReported] = useState(review.is_reported);
  const [busy, setBusy] = useState(false);

  const submitReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) {
      toast.error("Reply cannot be empty.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(replyEmail)) {
      toast.error("Enter the coach's contact email to verify.");
      return;
    }
    setBusy(true);
    try {
      const res = await coachlyApi.replyToReview(review.id, {
        coach_email: replyEmail.trim(),
        reply: replyText.trim(),
      });
      toast.success("Reply posted.");
      onUpdated?.(res.review);
      setReplyOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reply failed.");
    } finally {
      setBusy(false);
    }
  };

  const report = async () => {
    if (reported) return;
    setBusy(true);
    try {
      await coachlyApi.reportReview(review.id, { reason: "Reported by visitor" });
      setReported(true);
      toast.success("Thanks — this review has been flagged for review.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Report failed.");
    } finally {
      setBusy(false);
    }
  };

  const hasReply = Boolean(review.coach_reply);
  const replyAt = review.coach_reply_at ? new Date(review.coach_reply_at).getTime() : null;
  const withinEditWindow = replyAt ? (Date.now() - replyAt) < 24 * 60 * 60 * 1000 : false;
  const canReply = !hasReply;
  const canEditReply = hasReply && withinEditWindow;
  const replyPrefill = coachEmail ? coachEmail : "";

  return (
    <motion.li
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      data-testid={COACH_PROFILE.reviewItem(review.id)}
      className="p-6 md:p-7 border-b border-slate-100 last:border-b-0"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-semibold text-[13px]">
              {(review.client_name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-medium text-[14px] text-slate-900 truncate">{review.client_name}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <StarsDisplay value={review.rating} size={12} />
                <span className="text-[11.5px] text-slate-500">{timeAgo(review.created_at)}</span>
              </div>
            </div>
          </div>
          {review.comment && (
            <p className="mt-4 text-[14.5px] leading-relaxed text-slate-700 whitespace-pre-wrap">
              {review.comment}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {reported && (
            <span
              data-testid={COACH_PROFILE.reviewReportedFlag(review.id)}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-1"
            >
              <ShieldAlert className="w-3 h-3" /> Under review
            </span>
          )}
          <button
            onClick={report}
            disabled={busy || reported}
            data-testid={COACH_PROFILE.reviewReportBtn(review.id)}
            className="p-2 -m-1 text-slate-400 hover:text-red-500 disabled:hover:text-slate-400 disabled:opacity-40 transition-colors"
            title="Report this review"
            aria-label="Report review"
          >
            <Flag className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Existing coach reply */}
      {review.coach_reply && (
        <div
          data-testid={COACH_PROFILE.reviewReply(review.id)}
          className="mt-5 ml-13 pl-4 border-l-2 border-[#2563EB]/30 bg-slate-50/60 rounded-r-xl py-3 pr-4"
        >
          <div className="text-[11px] tracking-[0.24em] uppercase text-[#2563EB] font-semibold">
            Reply from the coach · {timeAgo(review.coach_reply_at)}
          </div>
          <p className="mt-2 text-[13.5px] leading-relaxed text-slate-700 whitespace-pre-wrap">
            {review.coach_reply}
          </p>
        </div>
      )}

      {/* Reply / Edit-reply CTA */}
      {(canReply || canEditReply) && !replyOpen && (
        <div className="mt-4">
          <button
            onClick={() => { setReplyOpen(true); setReplyEmail(replyPrefill); setReplyText(review.coach_reply || ""); }}
            data-testid={COACH_PROFILE.reviewReplyBtn(review.id)}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-500 hover:text-[#2563EB] transition-colors"
          >
            <MessageSquareReply className="w-3.5 h-3.5" strokeWidth={2} />
            {canEditReply ? "Edit reply (within 24h)" : "Reply as coach"}
          </button>
        </div>
      )}

      <AnimatePresence>
        {replyOpen && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            onSubmit={submitReply}
            data-testid={COACH_PROFILE.reviewReplyForm(review.id)}
            className="mt-4 overflow-hidden"
          >
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">Coach reply</div>
                <button
                  type="button"
                  onClick={() => setReplyOpen(false)}
                  className="p-1 -m-1 text-slate-400 hover:text-slate-900"
                  aria-label="Cancel reply"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <input
                type="email"
                value={replyEmail}
                onChange={(e) => setReplyEmail(e.target.value)}
                data-testid={COACH_PROFILE.reviewReplyEmail(review.id)}
                placeholder="Coach contact email"
                className="mt-3 w-full h-10 rounded-lg bg-white border border-slate-200 px-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
                required
              />
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                data-testid={COACH_PROFILE.reviewReplyInput(review.id)}
                rows={3}
                maxLength={1200}
                placeholder="Thanks Sam, glad the footwork drills clicked…"
                className="mt-2 w-full rounded-lg bg-white border border-slate-200 px-3 py-2 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25 resize-y"
                required
              />
              <div className="mt-3 flex items-center justify-between gap-3">
                <span className="text-[11px] text-slate-400">Editable within 24h of first reply.</span>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid={COACH_PROFILE.reviewReplySubmit(review.id)}
                  className="h-9 px-4 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[12.5px] font-semibold transition-colors disabled:opacity-60"
                >
                  {busy ? "Posting…" : "Post reply"}
                </button>
              </div>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </motion.li>
  );
}
